/**
 * SplashScreen — Écran de démarrage premium noir + logotype doré animé
 *
 * Design : "outfit" doré sur fond noir avec animation "shine" qui passe en boucle.
 * Conforme aux règles Apple : le launch screen natif (splash.png) reste statique,
 * cet écran le remplace dès que React Native est prêt.
 */
import React, { useEffect, useRef } from 'react';
import {
  View, Text, StyleSheet, Animated, Dimensions, Image,
} from 'react-native';
import { getCurrentLang } from '../i18n';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');
const BAR_WIDTH = SCREEN_WIDTH * 0.55;

export default function SplashScreen({ progress = 0, label }) {
  // v1.12.267 — getCurrentLang() pour éviter "useI18n must be used within I18nProvider"
  // (SplashScreen peut être rendu avant le Provider au démarrage)
  const lang = (() => { try { return getCurrentLang() || 'en'; } catch { return 'en'; } })();
  const _label = label || (lang === 'en' ? 'Initializing…' : 'Initialisation…');
  const progressAnim = useRef(new Animated.Value(0)).current;
  const fadeAnim     = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(progressAnim, {
      toValue: Math.max(0, Math.min(1, progress)),
      duration: 500,
      useNativeDriver: false,
    }).start();
  }, [progress]);

  useEffect(() => {
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 600,
      useNativeDriver: true,
    }).start();
  }, []);

  const width = progressAnim.interpolate({
    inputRange:  [0, 1],
    outputRange: ['0%', '100%'],
  });

  return (
    <View style={s.root}>
      <Animated.View style={[s.logoBlock, { opacity: fadeAnim }]}>
        {/* v1.12.843 — Logo splash discret et luxe : 220×220 (au lieu de 420×420) */}
        <Image
          source={require('../../assets/logo-app.png')}
          style={{ width: 220, height: 220, resizeMode: 'contain' }}
        />
        <View style={s.divider} />
        <Text style={s.tagline}>{lang === 'en' ? 'Your personal stylist' : 'Votre styliste personnel'}</Text>
      </Animated.View>

      <Animated.View style={[s.progressBlock, { opacity: fadeAnim }]}>
        <View style={s.progressBg}>
          <Animated.View style={[s.progressFill, { width }]} />
        </View>
        <Text style={s.progressLabel}>{_label}</Text>
      </Animated.View>
    </View>
  );
}

const s = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: '#0E0C0A',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoBlock: { alignItems: 'center', justifyContent: 'center' },
  divider: {
    width: 40, height: 1, backgroundColor: '#D4AF72',
    opacity: 0.5, marginTop: 20, marginBottom: 14,
  },
  tagline: {
    fontSize: 11, color: '#D4AF72', letterSpacing: 3,
    textTransform: 'uppercase', fontWeight: '400', opacity: 0.7,
  },
  progressBlock: {
    position: 'absolute', bottom: SCREEN_HEIGHT * 0.12,
    width: BAR_WIDTH, alignItems: 'center',
  },
  progressBg: {
    width: '100%', height: 2,
    backgroundColor: 'rgba(212, 175, 114, 0.2)',
    borderRadius: 2, overflow: 'hidden', marginBottom: 12,
  },
  progressFill: { height: '100%', backgroundColor: '#D4AF72', borderRadius: 2 },
  progressLabel: {
    fontSize: 10, color: '#D4AF72', letterSpacing: 2,
    textTransform: 'uppercase', fontWeight: '500', opacity: 0.8,
  },
});
